import * as React from "react";
// eslint-disable-next-line no-duplicate-imports
import { useEffect, useState, createRef } from "react";
import styled from "styled-components";
import {
  OriginThemeProvider,
  TrackingProvider,
} from "@origin-digital/ods-core";
import { LinkComponentProps } from "@origin-digital/ods-types";
import { trackingProviderCaptureClick } from "@origin-digital/reporting-client";
import { parseMarkdownEvent } from "@origin-digital/event-dispatcher";
import {
  SharedAppContextV2Consumer,
  SharedAppContextV2Provider,
} from "@origin-digital/shared-app-context";
import {
  ReviewAlertFooterService,
  TrustPilotModal,
} from "@origin-digital/platform-personalisation";

import { FontStyles, ResetStyles, IconFontStyles } from "../../styles/global";
import { Footer } from "../Footer";
import { KrakenAppContent, MyAccountAppContent } from "../AppContent";
import { SuppressAndroidInstallPrompt } from "../SuppressAndroidInstallPrompt";
import { CrisisBanner } from "../Banner";

const MeshLink = React.forwardRef<any, LinkComponentProps>((props, ref) => {
  const { href, onClick } = parseMarkdownEvent(props.href);
  return (
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    <a
      ref={ref}
      {...props}
      href={href}
      onClick={(e) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, babel/no-unused-expressions
        onClick && onClick(e);
        return props.onClick && props.onClick(e);
      }}
    />
  );
});

const ContentWrapper = styled.div`
  flex: 1 0 auto;
  ${({ background }: { background: "gray" | "white" | undefined }) => {
    if (background === "gray") return `background-color: rgb(241, 241, 241);`;
    if (background === "white") return `background-color: white;`;
    return "";
  }};
`;

const FooterWrapper = styled.div`
  flex-shrink: 0;
`;

//TODO: Type this exactly to match what tal-core passes on.
export interface AppProps {
  title: string;
  actionBar?: any;
  aemContent?: any;
  authenticated?: boolean;
  background?: "gray" | "white";
  channel?: string;
  children?: React.ReactNode;
  footer?: any;
  initSAC?: boolean;
  nav?: any;
  noSeed?: boolean;
  platform?: string;
  presets?: string[];
  showFooter?: boolean;
  showHeader?: boolean;
  showNativeAppAd?: boolean;
}

interface OetalApplicationRootProps {
  children?: React.ReactNode;
  showFooter?: boolean;
}

const ThemeProvider = ({
  children,
  noSeed,
}: Pick<AppProps, "children" | "noSeed">) => {
  return noSeed ? (
    <OriginThemeProvider linkComponent={MeshLink}>
      {children}
    </OriginThemeProvider>
  ) : (
    <OriginThemeProvider
      linkComponent={MeshLink}
      generatorClassNameOptions={{ seed: "tal" }}
    >
      {children}
    </OriginThemeProvider>
  );
};

// dev notes: OetalApplicationRoot sets a default height style to push the
// footer (if any) off the viewport and when an app is populated into it in the
// browser, the height style is removed.  This is done so the layout shift of
// the app forcing the footer down does not impact the core web-vital CLS
// figure.  As this default style is applied during SSR the resultant HTML will
// have that height in it.

// For applications that are longer than the viewport, the footer CLS shift is
// quite significant. Taking the footer off the viewport reduces it a lot.  For
// applications that are smaller than the viewport then the CLS reduction is not
// so much - but not many apps are smaller than the viewport.

// Note that this is only done when the footer is actually to be displayed and
// there are no children.  The only circumstance when there will be children is
// when a full-page SSR is underway - like with the home page.

// To detect an application being rendered as a child, we use a
// MutationObserver.  As soon as a child exists the height style is removed and
// the observer disconnected.
const OetalApplicationRoot = ({
  children,
  showFooter,
}: OetalApplicationRootProps) => {
  const [heightStyle, setHeightStyle] = useState(
    showFooter && !children ? { height: "2500px" } : {}
  );
  const ref = createRef<HTMLDivElement>();
  let observer;

  useEffect(() => {
    if (showFooter && !children) {
      const { current } = ref;
      const callback = (
        mutationsList: any,
        obs: { disconnect: () => void }
      ) => {
        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            setHeightStyle({});
            obs.disconnect();
          }
        }
      };

      observer = new MutationObserver(callback);
      observer.observe(current, {
        attributes: false,
        childList: true,
        subtree: false,
      });
    }
  }, []);
  return (
    <div id="oetal-application-root" ref={ref} style={heightStyle}>
      {children ? children : null}
    </div>
  );
};

const App = ({
  actionBar,
  authenticated = false,
  background,
  channel,
  children,
  footer,
  initSAC = false,
  nav,
  noSeed,
  platform,
  presets,
  showFooter = true,
  showHeader = true,
  showNativeAppAd = true,
  title,
}: AppProps) => {
  const isKraken =
    platform === "kraken" || (presets && presets.includes("kraken"));
  const isNativeChannel = channel === "native";

  const renderChildren = () => {
    const chld = children ? children : null;
    if (noSeed) {
      return chld;
    } else {
      return (
        <OriginThemeProvider generatorClassNameOptions={{ seed: "app" }}>
          {chld}
        </OriginThemeProvider>
      );
    }
  };
  return (
    <>
      <FontStyles />
      <ResetStyles />
      <IconFontStyles />

      <ThemeProvider noSeed={noSeed}>
        <TrackingProvider onTrackingCapture={trackingProviderCaptureClick}>
          <TrustPilotModal />
          <ReviewAlertFooterService />
          <ContentWrapper data-id="tal-content-wrapper" background={background}>
            {!showNativeAppAd && <SuppressAndroidInstallPrompt />}

            {!isNativeChannel && <CrisisBanner />}

            {/* P21-677 disable covid banner */}
            {/*{!isNativeChannel && isHomePage && <CovidBanner />}*/}

            {isKraken ? (
              <KrakenAppContent
                title={title}
                showHeader={showHeader}
                actionBar={actionBar}
              />
            ) : (
              <SharedAppContextV2Provider
                xhrHost={__DEV__ ? "http://localhost:4488" : undefined}
                xhrInitialize={initSAC}
              >
                <SharedAppContextV2Consumer>
                  {(context) => {
                    const { customerCode, premiseId, properties, isNative } =
                      context || {};

                    return (
                      <MyAccountAppContent
                        actionBar={actionBar}
                        authenticated={authenticated}
                        customerCode={customerCode}
                        isNative={isNative}
                        onSelectPremise={(premId) => {
                          context.actions.setPremiseId(premId);
                        }}
                        nav={nav}
                        showHeader={showHeader}
                        premiseId={premiseId}
                        properties={properties}
                        title={title}
                      />
                    );
                  }}
                </SharedAppContextV2Consumer>
              </SharedAppContextV2Provider>
            )}
            <OetalApplicationRoot showFooter={showFooter}>
              {renderChildren()}
            </OetalApplicationRoot>
          </ContentWrapper>
          {showFooter ? (
            <FooterWrapper>
              <Footer {...footer} />
            </FooterWrapper>
          ) : null}
        </TrackingProvider>
      </ThemeProvider>
    </>
  );
};

export { App };
